// ABOUTME: Context provider for the authenticated user (Me) and role management.
// ABOUTME: Provides user data and role switching functionality to child components; the hook lives in provider.ts.

import type { FC, ReactNode } from "react"
import { useEffect, useLayoutEffect, useMemo, useState } from "react"
import { useLocation } from "react-router"
import * as Sentry from "@sentry/react"
import { Role } from "@/graphql/main/gql/graphql"
import { useLocalStorage } from "@shared/hooks"
import { STORAGE_KEYS } from "@shared/constants/storageKeys"
import { useMe } from "./useMe"
import { MeContext, type MeContextType } from "./provider"
import { userSignedIn } from "./userSwitch"
import { CAppLoading, CButton } from "@shared/ui"
import { reloadApp } from "../reloadApp"
import { useTranslation } from "react-i18next"

const identifyForObservability = (userID: string, email: string): void => {
  Sentry.setUser({ id: userID, email })

  const { clarity } = window
  if (clarity) {
    clarity("identify", userID)
    clarity("set", "email", email)
  }
}

interface UserProviderProps {
  children: ReactNode
  /**
   * What to show while the profile request is in flight for someone who just came from the auth
   * screens. Passed in rather than imported: this provider is model-level and the screen belongs
   * to the auth pages, so importing it here was the layer reaching upwards.
   */
  authLoadingFallback?: ReactNode
  /** Ends the session for good — see useMe's onUserGone. */
  onUserGone: () => void
}

export const UserProvider: FC<UserProviderProps> = ({ children, authLoadingFallback, onUserGone }) => {
  const { me, loading, error } = useMe({ onUserGone })
  const { t } = useTranslation()
  const location = useLocation()
  const [currentRole, setCurrentRole] = useLocalStorage(STORAGE_KEYS.ROLE, Role.Athlete)
  const [showFallback, setShowFallback] = useState(false)
  const [isReloading, setIsReloading] = useState(false)

  // The wait is over: withdraw the failed-load screen in the render that sees it,
  // so a later wait starts from the loading screen again.
  if (!((loading && !me) || error) && showFallback) {
    setShowFallback(false)
  }

  useEffect(() => {
    if (!((loading && !me) || error)) return
    // Only flip to the "failed to load" screen when the wait is genuinely
    // long. A shorter window causes the error UI to flash for a second or
    // two on slow networks (3G) before the legit response arrives. `error`
    // starts the same timer — it does not jump to the fallback (see below).
    const timer = setTimeout(() => {
      setShowFallback(true)
    }, 20000)

    return () => clearTimeout(timer)
  }, [loading, me, error])

  // Validate role when user data is loaded - reset to Athlete if user lacks Trainer permissions
  // Use me.isCoach which checks both permissions.trainer AND role === Trainer
  useEffect(() => {
    if (me && !me.isCoach && currentRole === Role.Trainer) {
      setCurrentRole(Role.Athlete)
    }
  }, [me, currentRole, setCurrentRole])

  useEffect(() => {
    if (me) identifyForObservability(me.id, me.user.email)
  }, [me])

  // Another person can sign in without a reload — an expired session goes through the in-app sign-in
  // (model/navigation.ts redirectToLogin): what the one before left outside React, a message being written, goes.
  // A layout effect: the app under this provider mounts with the person after in the same commit, and its effects —
  // a discussion window taking the refusals kept for it — run before a parent's plain effect would.
  const meId = me?.id
  useLayoutEffect(() => {
    if (meId !== undefined) userSignedIn(meId)
  }, [meId])

  // Ensure role is valid for user's permissions
  const validatedRole = me && !me.isCoach && currentRole === Role.Trainer ? Role.Athlete : currentRole

  // Memoised so consumers re-render on a change of `me` or the role, not on every render of
  // the provider. `undefined` until `me` has loaded — the early returns below cover that.
  const value = useMemo<MeContextType | undefined>(
    () =>
      me
        ? {
            me,
            loading,
            currentRole: {
              isAthlete: validatedRole === Role.Athlete,
              isCoach: validatedRole === Role.Trainer,
              get: validatedRole,
              set: (role) => setCurrentRole(role),
            },
          }
        : undefined,
    [me, loading, validatedRole, setCurrentRole],
  )

  if (loading && !me && !showFallback) {
    // Arrived straight from the auth screens (goToApp / /auth/callback set the
    // state) — stay on their frame so the login → app hand-off doesn't flash a
    // white one. A cold open continues the start-up screen from index.html instead.
    const fromAuth = (location.state as { fromAuth?: boolean } | null)?.fromAuth
    // Say what is actually happening: the bundle has landed and React is running
    // — this wait is the profile request. "Загружаем приложение" here reads as if
    // the app were still downloading, which it is not.
    return fromAuth && authLoadingFallback ? authLoadingFallback : <CAppLoading note={t("common.loadingUserData")} />
  }

  // Show the failed-load screen ONLY after the 20s `showFallback` timer
  // has elapsed. We deliberately do NOT short-circuit on `error` alone:
  //   - Either underlying query (GetMe, UserOld — both cache-and-network) can
  //     emit a transient error while `me` is still undefined; `error` then
  //     flips truthy for a render, and short-circuiting on it would flash the
  //     fallback screen on the page opens that hit one.
  //   - The timer below already starts on `error`, so a *persistent*
  //     error still surfaces after 20s; a transient one is cancelled by
  //     the cleanup before the timer ever fires.
  if (!me && showFallback) {
    // Rage-click sentinel: reloadApp waits ~1s before actually reloading, and
    // without visible feedback users mash the button. Flip a local flag on
    // click to show a spinner/disabled state immediately.
    const handleReload = () => {
      if (isReloading) return
      setIsReloading(true)
      reloadApp()
    }
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4 text-center">
        <p className="text-lg font-medium text-black">{t("common.failedLoadDataDescription")}</p>
        <CButton
          size="md"
          className=" bg-black text-white"
          onClick={handleReload}
          disabled={isReloading}
          isProcessing={isReloading}
          label={t("common.repeat2")}
        />
      </div>
    )
  }

  if (!value) return null

  return <MeContext.Provider value={value}>{children}</MeContext.Provider>
}
